import { memo } from 'react'
import {
  Users,
  MessageSquare,
  Package,
  Image,
  NotebookText,
  Info,
  Settings,
  Sparkles,
  Star,
  User
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ThemeToggle } from '../components/ThemeToggle'
import TopNavBar from '../components/TopNavBar'
import { ComandosDaRodada } from '../turno/ComandosDaRodada'
import { FilaDoTurno } from '../turno/FilaDoTurno'
import SidePanel from '../components/SidePanel'
import { ChatInputContainer } from '../GameControls/ChatInputContainer'
import { cn } from '@/lib/utils'
import { isProfessorOrAdmin } from '@/utils/permissions'
import type { User as UserType } from '@/services/api'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'

interface DesktopLayoutProps {
  currentUser?: UserType
  activeTab: 'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes'
  totalUnread: number
  groupUnread?: number
  onTabChange: (tab: 'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes') => void
  onNavigate: (path: string) => void

  // TopNavBar / Turn props saíram — TopNavBar e ChatInputContainer
  // leem direto de TurnContext (turnState, currentPlayer, permissions,
  // players, onAdjustTime, onNextPlayer, onEndTurn, onStartTurn,
  // onRotatePlayer, onPauseTurn, formatTime, configuredTurn, onStartCountdown)

  // SidePanel props
  members: MemberType[]
  characters: Character[]
  tableName?: string
  /** Missao ativa no formato "Missao 3: O laboratorio" — sobe para a TopNavBar */
  missionTitle?: string
  userFullName: string
  userRole: string
  onCharacterChatClick: (character: Character) => void
  currentUserId?: string
  unreadCounts: Record<string, number>

  // Content (children)
  children: React.ReactNode

  // ChatInput props. A maioria saiu — o ChatInputContainer lê do ChatContext
  // (onRollDice/onLiveSync/onPoll/onLab/isConnected/loadingHistory/
  // loadingPrivateHistory/isMaster/respondingAsNpc/onToggleRespondAsNpc) e do
  // TurnContext. isLoading e selectedPrivateUser ainda são passados.
  isLoading: boolean
  selectedPrivateUser?: MemberType | null
  tableId?: string
  isUserOnline?: (userId: string) => boolean
  onOpenSettings: () => void

  // Criação mediada (fase 3a): sorteio na lista + mute do aluno sem ficha
  sorteioMediada?: boolean
  /** Modulo "dice" ativo na mesa (docs/PLANO_MODULO_DADOS.md). */
  dadosAtivos?: boolean
  onSortear?: (userId: string) => void
  sorteandoUserId?: string | null
  chatDisabled?: boolean
}

// Custom comparison for React.memo - focus on frequently changing props
function arePropsEqual(prev: DesktopLayoutProps, next: DesktopLayoutProps): boolean {
  // UI state
  if (prev.activeTab !== next.activeTab) return false
  if (prev.totalUnread !== next.totalUnread) return false
  if (prev.groupUnread !== next.groupUnread) return false

  // Loading state
  if (prev.isLoading !== next.isLoading) return false

  // User info
  if (prev.currentUser?.id !== next.currentUser?.id) return false
  if (prev.currentUserId !== next.currentUserId) return false

  // Selected entities
  if (prev.selectedPrivateUser?.userId !== next.selectedPrivateUser?.userId) return false

  // Arrays by reference
  if (prev.members !== next.members) return false
  if (prev.characters !== next.characters) return false
  if (prev.unreadCounts !== next.unreadCounts) return false

  // Presenca: sem esta comparacao um update de online/offline nao
  // re-renderizava o painel de membros (indicadores ficavam congelados).
  if (prev.isUserOnline !== next.isUserOnline) return false

  // Children (tabContent memoizado no GameSession — so muda quando o
  // conteudo real do chat/aba muda)
  if (prev.children !== next.children) return false

  // Static props - only check if they exist
  if (prev.tableId !== next.tableId) return false
  if (prev.tableName !== next.tableName) return false
  if (prev.missionTitle !== next.missionTitle) return false

  // Criação mediada (sorteio + mute)
  if (prev.sorteioMediada !== next.sorteioMediada) return false
  if (prev.dadosAtivos !== next.dadosAtivos) return false
  if (prev.sorteandoUserId !== next.sorteandoUserId) return false
  if (prev.chatDisabled !== next.chatDisabled) return false

  return true
}

export const DesktopLayout = memo(function DesktopLayout({
  currentUser,
  activeTab,
  totalUnread,
  groupUnread = 0,
  onTabChange,
  onNavigate,
  members,
  characters,
  tableName,
  missionTitle,
  userFullName,
  userRole,
  onCharacterChatClick,
  currentUserId,
  unreadCounts,
  children,
  isLoading,
  selectedPrivateUser,
  tableId,
  isUserOnline,
  onOpenSettings,
  sorteioMediada,
  dadosAtivos,
  onSortear,
  sorteandoUserId,
  chatDisabled
}: DesktopLayoutProps) {
  return (
    <>
      {/* SIDEBAR NAVEGAÇÃO - Desktop (Narrow) */}
      <aside className="hidden md:flex w-16 flex-col bg-game-secondary/80 backdrop-blur-xl border-r border-game-primary flex-shrink-0">
        {/* Topo: Avatar do usuário */}
        <div className="h-16 flex items-center justify-center border-b border-game-primary">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="relative group">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 cursor-pointer hover:scale-105 transition-transform">
                  <span className="text-white text-sm font-bold">
                    {currentUser?.full_name?.charAt(0) || currentUser?.username?.charAt(0) || 'U'}
                  </span>
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-game-primary dark:border-zinc-900" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" className="bg-game-secondary dark:bg-zinc-900/95 backdrop-blur-xl border-game-border-primary dark:border-white/[0.1]">
              <div className="px-3 py-2 border-b border-game-border-primary dark:border-white/[0.05]">
                <div className="text-sm font-semibold text-game-primary dark:text-zinc-100">{currentUser?.full_name || currentUser?.username}</div>
                <div className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                  <Star className="h-3 w-3" /> {currentUser ? isProfessorOrAdmin(currentUser) ? 'Mestre' : 'Jogador' : 'Jogador'}
                </div>
              </div>
              <DropdownMenuItem 
                className="text-game-secondary dark:text-zinc-200 focus:bg-game-hover dark:focus:bg-white/[0.05] focus:text-game-primary dark:focus:text-zinc-100"
                onClick={() => onNavigate('/perfil')}
              >
                <User className="h-4 w-4 mr-2" /> Meu Perfil
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-game-secondary dark:text-zinc-200 focus:bg-game-hover dark:focus:bg-white/[0.05] focus:text-game-primary dark:focus:text-zinc-100"
                onClick={onOpenSettings}
              >
                <Settings className="h-4 w-4 mr-2" /> Configurações
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-game-secondary dark:text-zinc-200 focus:bg-game-hover dark:focus:bg-white/[0.05] focus:text-game-primary dark:focus:text-zinc-100"
                onClick={() => onNavigate(`/detalhes/${tableId}`)}
              >
                <Info className="h-4 w-4 mr-2" /> Detalhes da Mesa
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-game-border-primary dark:bg-white/[0.05]" />
              <DropdownMenuItem
                className="text-rose-400 focus:bg-rose-500/10 focus:text-rose-400"
                onClick={() => onNavigate('/pagina-inicial')}
              >
                Sair da mesa
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Navegação vertical */}
        <nav className="flex-1 py-4 flex flex-col items-center gap-2">
          {[
            { id: 'chat', icon: Users, label: 'Chat' },
            { id: 'private', icon: MessageSquare, label: 'Privado' },
            { id: 'items', icon: Package, label: 'Itens' },
            { id: 'abilities', icon: Sparkles, label: 'Habilidades' },
            { id: 'media', icon: Image, label: 'Mídias' },
            { id: 'notes', icon: NotebookText, label: 'Anotações' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id as 'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes')}
              className={cn(
                "w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-200 group relative",
                activeTab === tab.id
                  ? "text-violet-600 dark:text-violet-400 bg-violet-500/15"
                  : "text-game-muted hover:text-game-primary dark:hover:text-zinc-100 hover:bg-game-hover dark:hover:bg-white/[0.05]"
              )}
              title={tab.label}
            >
              <tab.icon className="h-5 w-5" />

              {/* Badge de mensagens não lidas no ícone Privado */}
              {tab.id === 'private' && totalUnread > 0 && (
                <div className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-in zoom-in-50 duration-200">
                  <span className="text-[10px] font-bold text-white leading-none">
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                </div>
              )}

              {/* Badge de mensagens novas no chat em grupo */}
              {tab.id === 'chat' && groupUnread > 0 && (
                <div className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-in zoom-in-50 duration-200">
                  <span className="text-[10px] font-bold text-white leading-none">
                    {groupUnread > 99 ? '99+' : groupUnread}
                  </span>
                </div>
              )}

              {/* Tooltip */}
              <span className="absolute left-full ml-3 px-2 py-1 text-xs font-medium text-white bg-slate-800 dark:bg-zinc-800 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-lg">
                {tab.label}
              </span>
              {/* Indicador ativo */}
              {activeTab === tab.id && (
                <span className="absolute left-0 w-0.5 h-6 bg-violet-500 rounded-r-full" />
              )}
            </button>
          ))}
        </nav>

        {/* Footer: Configurações e Logo */}
        <div className="py-4 flex flex-col items-center gap-3 border-t border-game-primary">
          {/* Botão de Tema */}
          <ThemeToggle
            showTooltip={true}
            className="w-11 h-11 rounded-xl flex items-center justify-center text-game-muted hover:text-game-primary dark:hover:text-zinc-100 hover:bg-game-hover dark:hover:bg-white/[0.05] transition-all duration-200"
          />

          {/* Botão de Configurações */}
          <button
            className="w-11 h-11 rounded-xl flex items-center justify-center text-game-muted hover:text-game-primary dark:hover:text-zinc-100 hover:bg-game-hover dark:hover:bg-white/[0.05] transition-all duration-200 group relative"
            title="Configurações"
            onClick={onOpenSettings}
          >
            <Settings className="h-5 w-5" />
            <span className="absolute left-full ml-3 px-2 py-1 text-xs font-medium text-white bg-slate-800 dark:bg-zinc-800 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-lg">
              Configurações
            </span>
          </button>

          {/* Logo RPG-IA */}
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
        </div>
      </aside>

      {/* COLUNA 2: SIDEPANEL - Altura total (Desktop) */}
      <div className="hidden md:flex w-80 flex-col bg-game-secondary dark:bg-zinc-900/80 backdrop-blur-xl border-r border-game-border-primary dark:border-zinc-800/50 h-full flex-shrink-0">
        <SidePanel
          members={members}
          characters={characters}
          tableId={tableId}
          showHeader={true}
          tableName={tableName}
          userFullName={userFullName}
          userRole={userRole}
          onCharacterChatClick={onCharacterChatClick}
          currentUserId={currentUserId}
          unreadCounts={unreadCounts}
          isUserOnline={isUserOnline}
          sorteioMediada={sorteioMediada}
          dadosAtivos={dadosAtivos}
          onSortear={onSortear}
          sorteandoUserId={sorteandoUserId}
        />
      </div>

      {/* COLUNA 3: CONTAINER CENTRAL (TopNavBar + Chat) */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* TopNavBar lê turnState/permissions/etc do TurnContext; a missão
            ativa vem por prop (o nome da mesa já está no SidePanel) */}
        <TopNavBar missionTitle={missionTitle} />

        {/* A fila da rodada, visivel para todo mundo (fase 5) */}
        <FilaDoTurno currentUserId={currentUserId} />

        {/* Área Principal - Chat/Conteúdo */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Conteúdo das Abas - altura controlada, children deve ter h-full */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {children}
          </div>

          {/* Os comandos da rodada, logo acima da caixa de mensagem — o
              mesmo lugar do mobile, para o mestre nao ter de reaprender a
              tela ao trocar de aparelho. */}
          <ComandosDaRodada />

          {/* Input de Chat - Desktop: fixo embaixo */}
          <div className="flex-shrink-0 border-t border-zinc-800/80">
            {/* Props redundantes (onRollDice/onLiveSync/onPoll/onLab/isConnected/
                loadingHistory/loadingPrivateHistory/isMaster/respondingAsNpc/
                onToggleRespondAsNpc) saíram: o ChatInputContainer lê do ChatContext. */}
            <ChatInputContainer
              disabled={isLoading || chatDisabled}
              isLoading={isLoading}
              selectedPrivateUser={selectedPrivateUser}
              currentUserId={currentUserId}
              tableId={tableId}
              characters={characters}
              members={members}
            />
          </div>
        </div>
      </div>

    </>
  )
}, arePropsEqual)